import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/auth";
import { LOGO } from "@/data";

export default function Login() {
  const { login, user, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!checking && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] flex items-center justify-center px-5" data-testid="login-page">
      <div className="aurora-blob top-[-4rem] left-[10%] h-64 w-64 bg-[#F48220] opacity-30" />
      <div className="aurora-blob bottom-[-4rem] right-[10%] h-72 w-72 bg-[#3A53A3] opacity-30" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:p-10"
      >
        <img src={LOGO} alt="DNA Digital" className="h-10 w-auto object-contain" />
        <div className="mt-8 flex items-center gap-2 text-white/50">
          <Lock className="h-4 w-4" />
          <span className="font-body text-xs font-bold uppercase tracking-[0.2em]">Admin Access</span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white">Leads Dashboard</h1>
        <p className="mt-2 font-body text-sm text-white/50">Sign in to view captured leads.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label className="font-body text-sm text-white/70">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@dnadigital.in" className="bg-white/5 border-white/15 text-white placeholder:text-white/30" />
          </div>
          <div className="space-y-2">
            <Label className="font-body text-sm text-white/70">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="bg-white/5 border-white/15 text-white placeholder:text-white/30" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#F48220] px-6 py-3.5 font-body font-semibold text-black disabled:opacity-60 hover-lift"
          >
            {loading ? "Signing in..." : "Sign In"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>
      </motion.div>
    </main>
  );
}
